import {
  chakra,
  FormControl,
  FormErrorMessage,
  FormLabel,
  keyframes,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  IAttach,
  IResourceExemptions,
  useRequestNewResource,
} from '../../../../../services/hooks/Resources/useGetRequestNewResource';
import InputSelectMobile from '../../../../../components/Input/Mobile/Select';
import { useGetProofSubjects } from '../../../../../services/hooks/Resources/useGetProofSubjects';
import { FaClipboardList } from 'react-icons/fa';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { validateMaxFile, validateTypeFile, validationSizeFile } from '../../../../../utils/validationForm';
import { GoNumber } from 'react-icons/go';
import InputMobile from '../../../../../components/Input/Mobile';
import { useCreateResource } from '../../../../../services/hooks/Resources/useCreateResource';
import { memo, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usecreateImpugnation } from '../../../../../services/hooks/Impugnation/useCreateImpugnation';
import InformationCardCreateResource from './informationCard';
import InformationTitlesResourceCreate from './informationTitles';
import InformationExemptionCreateResource from './informationExemption';
import InputAttachmentFile from './attachmentFile';
import MessageBox from './message';
import NavigationButtonMobilePage from '../../../../../components/Mobile/navigationButton';
import {
  IExaminationBoardResponse,
  useGetExaminationBoard,
} from '../../../../../services/hooks/ExaminationBoard/useGetExaminationBoard';
import { useAuditLogger } from '../../../../../services/hooks/Audit';
import { useRequestMoreDetailResources } from '../../../../../services/hooks/Resources/useRequestMoreDetailResources';
import { useUpdateResource } from '../../../../../services/hooks/Resources/useUpdateResource';
import { IResponseIdName } from '../../../../../services/hooks/globalQuery';

const floatIn = keyframes`
  from {
    opacity: 0;
    transform: 0;
  }
  to {
    opacity: 1;
    height: 100%;
  }
`;

const ModalContentAnimation = chakra(ModalContent, {
  baseStyle: {
    animation: `${floatIn} 600ms cubic-bezier(.2,.9,.3,1) both`,
  },
});

interface IOwnProps {
  isImpugnation: boolean;
  selectiveProcess?: string;
  id: string;
  onOpen(): void;
  onClose(): void;
  isOpen: boolean;
  resourceId?: string;
}

const CreateResourceMobilePage: React.FC<IOwnProps> = ({
  isImpugnation,
  selectiveProcess,
  id,
  onOpen,
  onClose,
  isOpen,
  resourceId,
}) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const accent = '#222';

  const { data, refetch } = useRequestNewResource(id);
  const resourceRetriver = useRequestMoreDetailResources(resourceId || '', true);
  const { mutateAsync, isPending } = useUpdateResource(resourceId || '');

  const subjectData = useGetProofSubjects(
    { selective_process_id: data?.selective_process_id || '', registration: data?.registration.id },
    false,
  );

  const examinationBoardList = useGetExaminationBoard<IExaminationBoardResponse[]>({
    remove_pagination: true,
    selective_process: data?.selective_process_id?.toString(),
    status: 'published',
    show_members: true,
    resource_period_id: data?.resource_period_id,
  });

  const impugnation = usecreateImpugnation(id);
  const resource = useCreateResource(
    data?.resource?.id,
    data?.vacancy?.id,
    data?.resource_period_id,
    data?.selective_process_id,
  );

  const verifyTypeExemption = (): IResourceExemptions | undefined => {
    if (resourceId) {
      return {
        attachements: [],
        id: resourceRetriver.data?.exemption?.id || '',
        show_nis_field: false,
        show_redome_field: true,
        type: resourceRetriver.data?.exemption?.type?.id || '',
        justification: resourceRetriver.data?.exemption?.justification || '',
      };
    }

    let response = undefined;

    if (data?.exemptions) {
      data?.exemptions
        .filter((exemption) => hookforms.watch('exemption') === exemption.id)
        .map((ex) => {
          response = ex;
        });
    }

    return response;
  };

  const listExemption = useMemo(
    () => data?.exemptions?.map((exemption) => ({ id: exemption.id, name: exemption.type })) || [],
    [data?.exemptions],
  );

  const attachmentFiles = useMemo(() => {
    return (
      resourceRetriver.data?.attachments?.resource_file?.map(
        (file): IAttach => ({
          name: file.name,
          id: file.id,
          url: file.url,
          status: file.status,
          created_at: file.created_at,
          sent_by_client: file.sent_by_client,
          size: file.size,
        }),
      ) || []
    );
  }, [resourceRetriver.data]);

  useEffect(() => {
    if (data?.resource?.has_question) {
      subjectData.refetch();
    }
  }, [data, resourceRetriver.data]);

  const schema = Yup.object().shape({
    exemption: Yup.string().when({
      is: () => data?.exemptions && data.exemptions.length > 0,
      then: (schema) => schema.required('Campo obrigatorio'),
    }),
    attachmentFile: Yup.mixed()
      .when({
        is: () => data?.is_required_attachment,
        then: (schema) => schema.required('Envio de arquivo obrigatório'),
      })
      .test('invalidExtension', 'Verifique o nome do arquivo enviado', (file) => {
        const listFile = file as FileList;
        let hasError = true;

        for (let index = 0; index < listFile?.length; index++) {
          const hasDot = listFile[index].name.split('.');
          if (hasDot.length > 2) hasError = false;
        }

        return hasError;
      })
      .test('validationSizeFile', `Tamanho máximo para arquivos é de ${data?.size_attached}MB`, (file) => {
        const listFile = file as FileList;

        if (!data?.resource.has_attachment) return true;

        if (listFile?.length > 0) {
          const response = validationSizeFile(listFile, data?.size_attached);
          return response;
        }

        return true;
      })
      .test('validationMaxFile', `Quantidade máxima de arquivos é de ${data?.max_attached} arquivos`, (file) => {
        const listFile = file as FileList;
        if (!data?.resource.has_attachment) return true;

        if (listFile?.length > 0) {
          return validateMaxFile(listFile, data?.max_attached);
        }
        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
        const listFile = file as FileList;
        if (!data?.resource.has_attachment) return true;
        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      })
      .test('required', 'Envio de arquivo obrigatório', (file) => {
        if (!data?.resource.has_attachment) return true;
        const listFile = file as FileList;

        if (data?.is_required_attachment) {
          if (listFile.length === 0) {
            return false;
          }
        }

        return true;
      }),

    detail: Yup.string().when({
      is: () => data?.resource?.has_observation || isImpugnation,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    nis: Yup.string().when({
      is: () => verifyTypeExemption()?.type.toLowerCase().includes('cadúnico'),
      then: (schema) => schema.required('Campo obrigatório').length(11, 'Campo NIS deve conter 11 digitos'),
    }),
    redome_code: Yup.string().when({
      is: () => verifyTypeExemption()?.type.toLowerCase().includes('medula'),
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    questionNumber: Yup.string().when({
      is: () => data?.resource?.has_question,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('verifyQuestion', 'Questão não pertence a prova informada', (question) => {
            if (data?.resource?.has_question) {
              if (question) {
                const subject = subjectData.data?.find((s) => s.id === hookforms.watch('proof_subject'));
                if (subject?.questions?.includes(question)) {
                  return true;
                } else {
                  return false;
                }
              }
            }

            return true;
          }),
    }),
    proof_subject: Yup.string().when({
      is: () => data?.resource?.has_question,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    title_category: Yup.string().when({
      is: () => data?.against_titles,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    title_item: Yup.string().when({
      is: () => data?.against_titles,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    examination_board_id: Yup.string().when({
      is: () => data?.against_examination_board,
      then: (schema) => schema.required('Campo obrigatório'),
    }),
    examination_board_member_id: Yup.string(),
  });

  const hookforms = useForm({
    resolver: yupResolver(schema),
    values: {
      exemption: resourceId
        ? resourceRetriver.data?.exemption?.id || ''
        : data?.exemptions?.length === 1
          ? data?.exemptions[0]?.id
          : '',
      detail: resourceRetriver.data?.observation || '',
      nis: resourceRetriver.data?.candidate?.nis || '',
      redome_code: resourceRetriver.data?.candidate?.redome_code || '',
      attachmentFile: attachmentFiles,
      examination_board_id: resourceRetriver.data?.examination_board?.id || '',
      examination_board_member_id: resourceRetriver.data?.examination_board?.member?.id || '',
      proof_subject: resourceRetriver.data?.proof_subject?.id || '',
      questionNumber: resourceRetriver.data?.question?.toString() || '',
      title_category: resourceRetriver.data?.titles?.category?.id || '',
      title_item: resourceRetriver.data?.titles?.item?.id || '',
    },
  });

  const { logWithBuilder } = useAuditLogger();

  const newResource = (payload: FieldValues) => {
    const endPoint = isImpugnation ? impugnation : resource;
    const invalidateKey = isImpugnation ? ['my_impugnation'] : ['detail_resource', id];

    if (isImpugnation) {
      payload = {
        objection: payload.detail,
      };
    } else if (data?.exemptions && data.exemptions.length === 0) {
      toast({
        title: 'Atenção',
        description: `Você atingiu o limite maximo de ${isImpugnation ? 'impugnações' : 'recursos'}`,
        status: 'info',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    endPoint
      .mutateAsync(payload)
      .then(async (response) => {
        toast({
          title: 'Sucesso',
          description: `${isImpugnation ? 'Sua impugnação' : 'Seu recurso'} foi enviado com sucesso`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });

        //LOG DE AUDITORIA
        await logWithBuilder((builder) =>
          builder
            .action(isImpugnation ? 'impugnation_created' : 'resource_created')
            .level('info')
            .category(isImpugnation ? 'impugnation' : 'resource')
            .resourceType(isImpugnation ? 'impugnation' : 'resource')
            .resourceId(response?.data?.id)
            .selectiveProcessId(data?.selective_process_id || '')
            .message(
              `Candidato enviou um ${isImpugnation ? 'impugnação' : 'recurso'}. Justificativa: "${response?.data?.observation}" | Tipo de recurso: ${response?.data?.resource_type?.name} | Anexos: ${response?.data?.attachments?.resource_file?.length || '0'} `,
            ),
        );

        queryClient.invalidateQueries({ queryKey: invalidateKey, exact: true });
        hookforms.reset({
          attachmentFile: [],
          detail: '',
          exemption: '',
          nis: '',
          redome_code: '',
          examination_board_id: '',
          examination_board_member_id: '',
          proof_subject: '',
          questionNumber: '',
          title_category: '',
          title_item: '',
        });
        onClose();
      })
      .catch((error) => {
        toast({
          title: 'Atenção',
          description:
            error?.response?.data?.message ||
            `Erro ao enviar o ${isImpugnation ? 'impugnação' : 'recurso'}, tente novamente`,
          status: 'warning',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const updateResource = async (payload: FieldValues) => {
    const existingIds = resourceRetriver.data?.attachments?.resource_file?.map((f) => f.id) || [];

    const newAttachmentFiles =
      payload.attachmentFile
        ?.filter((item: IResponseIdName) => !existingIds.includes(item.id))
        .map((file: File) => ({
          id: '',
          file,
        })) || [];

    const currentIds = payload.attachmentFile?.map((item: IResponseIdName) => item.id) || [];

    const removedAttachmentFiles =
      resourceRetriver.data?.attachments?.resource_file
        ?.filter((file) => !currentIds.includes(file.id))
        ?.map((file) => file.id) || [];

    try {
      await mutateAsync({
        attachmentFile: newAttachmentFiles,
        removeAttachmentFileIds: removedAttachmentFiles,
        payload: { ...payload },
      });
      await refetch();
      toast({
        title: 'Sucesso',
        description: 'Recurso atualizado com sucesso',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      hookforms.reset({
        attachmentFile: undefined,
        detail: payload.detail || resourceRetriver.data?.observation || '',
        proof_subject: resourceRetriver.data?.proof_subject?.id || '',
        title_category: '',
        title_item: '',
        examination_board_id: resourceRetriver.data?.examination_board?.id || '',
        examination_board_member_id: resourceRetriver.data?.examination_board?.member?.id || '',
        redome_code: resourceRetriver.data?.candidate?.redome_code || '',
      });

      onClose();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao atualizar o recurso, tente novamente',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const exemption = useMemo((): IResourceExemptions | undefined => {
    if (resourceId) {
      return {
        attachements: [],
        id: resourceRetriver.data?.exemption?.id || '',
        show_nis_field: resourceRetriver?.data?.exemption?.show_nis_field || false,
        show_redome_field: resourceRetriver?.data?.exemption?.show_redome_field || false,
        type: resourceRetriver.data?.exemption?.type?.name || '',
        justification: resourceRetriver.data?.exemption?.justification || '',
      };
    }

    let response = undefined;

    if (data?.exemptions) {
      data?.exemptions
        .filter((exemption) => hookforms.watch('exemption') === exemption.id)
        .map((ex) => {
          response = ex;
        });
    }

    return response;
  }, [data?.exemptions, resourceRetriver.data?.exemption, hookforms.watch('exemption')]);

  let titleError = undefined;

  if (data?.exemptions && data.exemptions.length === 0) {
    titleError = (
      <Text color="gray.400" fontSize="sm">
        Todos os recursos para <b>{data.period_identification}</b> já foram realizados. No momento não há novos recursos
        disponíveis.
      </Text>
    );
  } else if (data?.resource?.has_question && subjectData.data && subjectData.data.length === 0) {
    titleError = (
      <Text color="gray.400" fontSize="sm">
        Você não está apto para solicitar um recurso <b>{data.period_identification}</b>
      </Text>
    );
  }

  const hasAttachment = data?.resource?.has_attachment && data?.max_attached;

  const selectedExaminationBoard = examinationBoardList?.data?.find(
    (item) => item.id === hookforms.watch('examination_board_id'),
  );

  const selectedProofSubject = hookforms.watch('proof_subject');
  const questionList =
    subjectData?.data?.find((e) => e.id === selectedProofSubject)?.questions?.map((q) => ({ id: q, name: q })) || [];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" motionPreset="none">
        <ModalOverlay bg="blackAlpha.400" />
        <ModalContentAnimation
          position="absolute"
          bottom="0"
          w="100%"
          maxW="100dvw"
          maxH="90%"
          h={0}
          borderRadius="10px 10px 0 0"
          overflow="hidden"
          bgColor="white"
          shadow="xl"
        >
          <ModalHeader fontSize="lg" fontWeight="bold" borderBottomWidth="1px">
            Enviar {isImpugnation ? 'impugnação' : 'recurso'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={6}>
            {(!resourceId && titleError && <MessageBox>{titleError}</MessageBox>) || (
              <FormProvider {...hookforms}>
                <VStack spacing={6} align="stretch" h={isImpugnation ? '100%' : 'initial'}>
                  <InformationCardCreateResource
                    data={data}
                    isImpugnation={isImpugnation}
                    selectiveProcess={selectiveProcess}
                  />
                  <InformationTitlesResourceCreate
                    showCard={data?.against_titles}
                    title_category={hookforms.watch('title_category')}
                    registrationId={data?.registration.id.toString()}
                  />
                  {listExemption.length > 1 && (
                    <InputSelectMobile
                      name="exemption"
                      placeholderLabel="Selecione o tipo de isenção"
                      iconLeft={<FaClipboardList size={14} color={accent} />}
                      isRequired
                      listItens={listExemption}
                      fontSize={12}
                    />
                  )}
                  <InformationExemptionCreateResource selectedExemption={hookforms.watch('exemption')} />
                  {hookforms.watch('exemption') &&
                    exemption &&
                    exemption.show_nis_field &&
                    exemption.type?.toLowerCase()?.includes('cadúnico') && (
                      <InputMobile
                        name="nis"
                        placeholderLabel="Informe seu NIS"
                        iconLeft={<GoNumber size={15} color={accent} />}
                        isRequired
                        onlyNumber
                      />
                    )}

                  {hookforms.watch('exemption') &&
                    exemption &&
                    exemption.show_redome_field &&
                    exemption.type?.toLowerCase()?.includes('medula') && (
                      <InputMobile
                        name="redome_code"
                        placeholderLabel="Informe código REDOME"
                        iconLeft={<GoNumber size={15} color={accent} />}
                        isRequired
                        onlyNumber
                      />
                    )}
                  {data?.resource?.has_question && (
                    <Stack>
                      <Text
                        fontWeight="semibold"
                        mb={0}
                        borderBottom="1px solid"
                        borderColor="gray.400"
                        pb={0}
                        w="fit-content"
                        pr={4}
                        fontSize="md"
                      >
                        Dados da questão
                      </Text>
                      <InputSelectMobile
                        name="proof_subject"
                        placeholderLabel="Selecione a prova"
                        iconLeft={<FaClipboardList size={14} color={accent} />}
                        isRequired
                        listItens={subjectData.data}
                        fontSize={12}
                      />

                      <InputSelectMobile
                        name="questionNumber"
                        placeholderLabel="Selecione a questão"
                        iconLeft={<GoNumber size={14} color={accent} />}
                        isRequired
                        listItens={questionList}
                        fontSize={12}
                      />
                    </Stack>
                  )}
                  <Stack gap={4} h={isImpugnation ? '100%' : 'initial'} pb={isImpugnation ? 3 : 'initial'}>
                    <Text
                      fontWeight="semibold"
                      mb={0}
                      borderBottom="1px solid"
                      borderColor="gray.400"
                      pb={1}
                      w="fit-content"
                      pr={4}
                      fontSize="md"
                    >
                      Dadas para a solicitação {isImpugnation ? 'da impugnação' : 'do recurso'}
                    </Text>

                    {data?.against_examination_board && (
                      <>
                        <Stack w={['100%', '100%', '100%', '50%', '50%']}>
                          <InputSelectMobile
                            name="examination_board_id"
                            placeholderLabel="Selecione a banca:"
                            isRequired
                            listItens={examinationBoardList?.data}
                            fontSize={12}
                          />
                          {selectedExaminationBoard && (
                            <>
                              <InputSelectMobile
                                name="examination_board_member_id"
                                placeholderLabel="Selecione o membro da banca (opcional):"
                                listItens={[
                                  { id: '', name: 'Selecione' },
                                  ...(selectedExaminationBoard?.members?.map((member) => ({
                                    id: member.id,
                                    name: `${member?.colaborator_name} - ${member?.role_display?.toUpperCase()}`,
                                  })) ?? []),
                                ]}
                                fontSize={12}
                              />
                            </>
                          )}
                        </Stack>
                      </>
                    )}

                    <Stack>
                      {(data?.resource?.has_observation || isImpugnation) && (
                        <FormControl
                          isInvalid={!!hookforms.formState.errors.detail?.message}
                          sx={{
                            transition: 'padding-top 0.2s ease',
                            '&:has(input:focus), &:has(input:not(:placeholder-shown))': {
                              paddingTop: '0.8rem',
                            },
                          }}
                          isRequired={true}
                          position="relative"
                          h={isImpugnation ? '100%' : 'initial'}
                        >
                          <FormLabel fontWeight="semibold" mb={1} fontSize="13px" transition="all 0.2s ease">
                            Argumentação {isImpugnation ? 'da impugnação' : 'do recurso'}:
                          </FormLabel>
                          <Textarea
                            placeholder="Digite aqui sua argumentação..."
                            rounded="md"
                            px={2}
                            resize="vertical"
                            minH={isImpugnation ? '100px' : '120px'}
                            {...hookforms.register('detail')}
                          />
                          {hookforms.formState.errors.detail && (
                            <FormErrorMessage fontSize="0.78rem">
                              {hookforms.formState.errors.detail.message}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      )}
                      {hasAttachment && <InputAttachmentFile />}
                    </Stack>
                  </Stack>
                </VStack>
              </FormProvider>
            )}
          </ModalBody>

          <ModalFooter p={0}>
            <NavigationButtonMobilePage
              mainButtonText={
                data?.exemptions && data.exemptions.length === 0 && !resourceId
                  ? 'Fechar'
                  : `Enviar ${isImpugnation ? 'impugnação' : 'Recurso'}`
              }
              onGoMainButton={
                data?.exemptions && data.exemptions.length === 0 && !resourceId
                  ? onClose
                  : hookforms.handleSubmit(resourceId ? updateResource : newResource)
              }
              onGoBackButton={onClose}
              w={data?.exemptions && data.exemptions.length === 0 && !resourceId ? '40%' : undefined}
              isLoading={resource.isPending || impugnation.isPending || isPending}
              loadingText="Enviando..."
            />
          </ModalFooter>
        </ModalContentAnimation>
      </Modal>
      <NavigationButtonMobilePage
        absolute
        mainButtonText={`Adicionar ${isImpugnation ? 'nova impugnação' : 'novo recurso'}`}
        onGoMainButton={onOpen}
      />
    </>
  );
};

export default memo(CreateResourceMobilePage);
